import { Components, Data, EnumHelper, FileUtils, ITaskRunner, List, Misc, ModalUtils, Model, NotifyUtils } from "@singularsystems/neo-core";
import { Neo, NeoGrid, Views } from "@singularsystems/neo-react";
import { observer } from "mobx-react";
import React from "react";
import { NotificationDuration } from "../../../../App/Models/NotificationDuration";
import { AppService, DomainTypes, Types } from "../../../DomainTypes";
import Approval from "../../../Models/Approvals/InvestorInvestmentApproval";
import InternalApproverLookup from "../../../Models/Approvals/Lookups/InternalApproverLookup";
import { SourceOfFundsType } from "../../../Models/Approvals/SourceOfFundsType.enum";
import InvestorInvestment from "../../../Models/InvestorInvestments/InvestorInvestment";
import * as Roles from '../../../Models/Security/Roles';
import { CatalogueColumn } from "@singularsystems/neo-react-services/dist/Catalogue/Components/CatalogueFormGroup";
import InternalApprovalEntityCatalogueEntry from "../../Catalogue/GenericComponents/InternalApprovalEntityCatalogueEntry";
import GenerateTermsAndConditionsCommand, { TermsAndConditionsContact } from "../../../Models/InvestorInvestments/Commands/GenerateTermsAndConditionsCommand";
import ContactAutoCompleteDropdown from "../../Contacts/Components/ContactAutoCompleteDropdown";
import ContactLookup from "../../../Models/Contacts/ContactLookup";
import MergeTemplate from "../../../Models/Catalogue/General/MergeTemplate";

interface IApprovalComponentProps {
    taskRunner?: ITaskRunner;
    investorInvestment: InvestorInvestment;
}

@observer
export default class ApprovalComponent extends Views.ViewComponentBase<IApprovalComponentProps, ApprovalComponentVM> {

    constructor(props: IApprovalComponentProps) {
        super(props, () => new ApprovalComponentVM(props.investorInvestment, props.taskRunner));

    }

    public render() {
        const viewModel = this.viewModel;
        const approval = viewModel.approval;
        const canEdit = viewModel.authorisation.hasRole(Roles.InvestorInvestmentApproval.Edit);

        const actions = approval ? this.getActions(approval) : [];

        return (
            <div>
                {!approval &&
                    <div style={{ textAlign: "right" }} className="mb-3">
                        <Neo.Button
                            icon="far-file"
                            onClick={() => viewModel.beginGenerateTermsAndConditions()}
                            variant="secondary"
                            className="me-2"
                            hidden={!canEdit}>Manual Terms and Conditions</Neo.Button>

                        <Neo.Button
                            icon="stamp"
                            onClick={() => viewModel.createApproval()}
                            hidden={!canEdit}>New Approval</Neo.Button>
                    </div>}

                {approval &&
                    <>
                        <Neo.Card>
                            <div className="text-end mb-3">
                                {approval.termsAndConditionsFileDescriptorId &&
                                    <Neo.Button
                                        icon="file"
                                        className="min-width me-2"
                                        variant="secondary"
                                        tooltip="Download Terms and Conditions"
                                        onClick={() => viewModel.downloadTermsAndConditionsFile()}>
                                        TCs
                                    </Neo.Button>}
                                <Neo.Button
                                    icon="file"
                                    className="min-width me-2"
                                    variant="secondary"
                                    tooltip="Download Participation Agreement"
                                    onClick={() => viewModel.downloadParticipationAgreementFile()} >
                                    PA
                                </Neo.Button>

                                {actions.length > 0 && 
                                    <Neo.Button className="me-2" isOutline menuItems={actions}>Actions</Neo.Button>}

                                <Neo.Button
                                    icon="trash"
                                    variant="danger"
                                    tooltip="Delete Approval"
                                    className="min-width me-2"
                                    disabled={approval.completed.on != null}
                                    onClick={() => this.beginDelete()} />

                                <Neo.Button pulse={approval.readyToSave}
                                    variant="primary"
                                    icon="save"
                                    disabled={!approval.isDirty}
                                    onClick={() => this.viewModel.saveApproval()}>
                                    Save
                                </Neo.Button>
                            </div>
                            <Neo.GridLayout lg={2} xxl={4}>
                                <Neo.FormGroupInline label="Activated On" display={approval.activated.meta.on} dateProps={{ formatString: "dd MMM yyyy HH:mm"}} placeholder="Not yet activated" />
                                <Neo.FormGroupInline label="Client status" display={approval.meta.clientActivationStatus} />
                                <Neo.FormGroupInline label="Completed On" display={approval.completed.meta.on} dateProps={{ formatString: "dd MMM yyyy HH:mm"}} />
                                <Neo.FormGroupInline display={approval.meta.sourceOfFunds} select={{ items: EnumHelper.asList(SourceOfFundsType) }} 
                                    append={approval.proofOfPaymentFileDescriptorId ? <Neo.Button icon="file" variant="secondary" onClick={() => viewModel.downloadProofOfPaymentFile()}>View</Neo.Button> : undefined} />
                            </Neo.GridLayout>
                        </Neo.Card>
                        <Neo.Card>
                            <Neo.GridLayout xxl={2}>
                                <div>
                                    <NeoGrid.Grid items={approval.internalApprovals} showAddButton>
                                        {(item, meta) => (
                                            <NeoGrid.Row>
                                                <NeoGrid.Column bind={meta.internalApproverId} readOnly={approval.activated.on !== null} select={{ itemSource: viewModel.internalApproverDataSource }} />
                                                <CatalogueColumn bind={meta.internalApprovalEntityId} readOnly={approval.activated.on !== null} catalogueType={InternalApprovalEntityCatalogueEntry}
                                                    label='Entity' />
                                                <NeoGrid.Column label="Approved On" display={item.approved.meta.on} />
                                                <NeoGrid.ButtonColumn showDelete={!approval.activated.on} />
                                            </NeoGrid.Row>
                                        )}
                                    </NeoGrid.Grid>
                                </div>
                                
                                {approval.lookup.requiredClientAuthorisations > 0 &&
                                    <div>
                                        <NeoGrid.Grid items={approval.clientApprovals}>
                                            {(item, meta) => (
                                                <NeoGrid.Row>
                                                    <NeoGrid.Column label="Client User" display={meta.authorisedByName} />
                                                    <NeoGrid.Column label="Authorised On" display={item.authorised.meta.on} dateProps={{ formatString: "dd MMM yyyy HH:mm"}} />
                                                </NeoGrid.Row>
                                            )}
                                        </NeoGrid.Grid>
                                        <div className="mt-3">
                                            {approval.clientApprovals.length} / {approval.lookup.requiredClientAuthorisations} authorisation(s).
                                        </div>
                                    </div>}
                                
                            </Neo.GridLayout>
                        </Neo.Card>
                    </>}

                    <Neo.Modal title="Generate Terms and Conditions"
                        bindModel={viewModel.meta.termsAndConditionsCommand}
                        acceptButton={{ text: "Generate", onClick: () => viewModel.generateTermsAndConditions()}}>
                        {(command: GenerateTermsAndConditionsCommand) => (
                            <div>
                                <Neo.FormGroup bind={command.meta.mergeTemplateId} 
                                    select={{ 
                                        itemSource: viewModel.dataCache.mergeTemplates,
                                        onItemSelected: (item) => viewModel.onMergeTemplateChanged(command, item),
                                        filterPredicate: viewModel.mergeTemplateFilter }} />

                                {command.signingContacts.length > 0 &&
                                    <div className="mt-4">
                                        <h6>Signatories</h6>
                                        {command.signingContacts.map((contact) => (
                                            <Neo.FormGroupInline bindContext={contact.meta.contactId} label={contact.contactType} key={contact.contactType}>
                                                {contact.contactTypeId &&
                                                    <Neo.DropDown 
                                                        bind={contact.meta.contactId} 
                                                        select={{ 
                                                            items: viewModel.filteredContacts, 
                                                            filterPredicate: item => item.contactTypeId === contact.contactTypeId, 
                                                            onItemSelected: item => viewModel.onContactSelected(contact, item) }} />}
                                                {!contact.contactTypeId &&
                                                    <ContactAutoCompleteDropdown 
                                                        bind={contact.meta.contactId} 
                                                        bindDisplay={contact.meta.contactName} 
                                                        onChanged={item => viewModel.onContactSelected(contact, item)} 
                                                        taskRunner={viewModel.taskRunner} />}
                                            </Neo.FormGroupInline>
                                        ))}
                                    </div>}
                            </div>
                        )}
                    </Neo.Modal>
            </div>
        )
    }

    public getActions(approval: Approval): Components.IButtonDropDownItem[] {

        if (approval.investorInvestmentApprovalId && !approval.activated.on) {
            const items: Components.IButtonDropDownItem[] = [];
            if (!approval.activated.on) {
                let prefix = approval.termsAndConditionsFileDescriptorId ? "Re-" : "";
                items.push({ text: `${prefix}Generate Terms and Conditions`, onClick: () => this.viewModel.beginGenerateTermsAndConditions() });
                items.push({ text: `${prefix}Upload Terms and Conditions`, onClick: () => this.viewModel.beginUploadTermsAndConditions() });
            }

            if (approval.termsAndConditionsFileDescriptorId) {
                items.push({ text: "Activate", onClick: () => this.viewModel.toggleActivation() });
            }

            return items;
        } else if (approval.clientApproved.on !== null && approval.clientApprovals.length >= approval.lookup.requiredClientAuthorisations 
            && !approval.completed.on) {
            return [{ text: "Complete", onClick: () => this.viewModel.completeApproval() }];
        }
        return [];
    }

    public beginDelete() {
        if (this.viewModel.approval?.isNew) {
            this.viewModel.approval = null;
        } else {
            ModalUtils.showYesNoDismissible("Delete", "Are you sure you want to delete this approval?", () => this.viewModel.deleteApproval());
        }
        
    }
}

class ApprovalComponentVM extends Views.ViewModelBase {

    constructor(
        public investorInvestment: InvestorInvestment,
        taskRunner: ITaskRunner = AppService.get(Types.Neo.TaskRunner),
        public dataCache = AppService.get(DomainTypes.Services.DataCache),
        private approvalApiClient = AppService.get(DomainTypes.ApiClients.InvestorInvestmentApproval),
        private contactsApiClient = AppService.get(DomainTypes.ApiClients.Contact),
        public internalApproverApiClient = AppService.get(DomainTypes.ApiClients.InternalApprover),
        public documentsApiClient = AppService.get(DomainTypes.ApiClients.Documents),
        public authorisation = AppService.get(Types.Neo.Security.AuthorisationService)) {
        super(taskRunner);
        this.makeObservable();

        this.mergeTemplateFilter = this.mergeTemplateFilter.bind(this);
    }

    public approval: Approval | null = null;

    public termsAndConditionsCommand: GenerateTermsAndConditionsCommand | null = null;

    public internalApproverDataSource = new Data.ApiClientLookupDataSource(this.internalApproverApiClient.getInternalApproverLookups, InternalApproverLookup);
    public filteredContacts = new List(ContactLookup);

    public termsFileManager = new Components.FileManager(
        this.approvalApiClient.getUploadTermsAndConditionsUrl(), 
        { 
            afterUpload: this.afterTermsAndConditionsUploaded.bind(this), 
            additionalData: () => ({ approvalId: this.approval!.investorInvestmentApprovalId, investorId: this.investorInvestment.investorId! }),
            taskRunner: this.taskRunner 
        });

    public async initialise() {
        await this.fetchApproval();
    }

    public async fetchApproval() {
        if (this.investorInvestment.lookup.approvalId) {
            const data = await this.taskRunner.waitForData(this.approvalApiClient.getApproval(this.investorInvestment.lookup.approvalId));
            this.approval = Approval.fromJSObject<Approval>(data);
        }
        else {
            this.approval = null;
        }
    }

    public createApproval() {
        if (!this.investorInvestment.investorInvestmentId) {
            NotifyUtils.addDanger("Create Approval", "Please save the investment first.", NotificationDuration.Standard);
            return;
        }

        return this.taskRunner.run(async () => {

            if (!this.approval) {
                const result = await this.approvalApiClient.create(this.investorInvestment.investorInvestmentId);
                this.approval = Approval.fromJSObject<Approval>(result.data);
                NotifyUtils.addSuccess("Create approval", "Please select approvers and click save.", NotificationDuration.Standard);
            }
        });
    }

    public saveApproval() {

        // Not in a form, so must check manually.
        if (!this.approval?.isValid) {
            ModalUtils.showMessage("Invalid", <Neo.ValidationSummary model={this.approval!} />);
            return;
        }

        this.taskRunner.run(async () => {
            if (this.approval) {
                var result = await this.approvalApiClient.save(this.approval.toJSObject());
                this.approval.update(result.data);
                this.investorInvestment.lookup.approvalId = this.approval.investorInvestmentApprovalId;
                this.approval.markOld();
                NotifyUtils.addSuccess("Saved", "The approval was saved successfully.", NotificationDuration.Standard);
            }
        });
    }

    public async toggleActivation() {

        if (this.approval) {
            if (this.approval.isDirty) {
                NotifyUtils.addInfo("Activate", "Please save the approval first", NotificationDuration.Standard);
            } else {
                const result = await this.taskRunner.run(() => this.approvalApiClient.toggleActivation(this.approval!.investorInvestmentApprovalId));
                this.approval.activated = Model.UserEvent.fromJSObject<Model.UserEvent>(result.data);
                this.approval.markOld();
                NotifyUtils.addSuccess("Approval activated successfully.", "", NotificationDuration.Standard);
            }
        }
    }

    public async completeApproval() {

        if (this.approval?.internalApprovals.some(c => !c.approved.on)) {
            const result = await ModalUtils.showYesNoDismissible("Complete Approval", "Some internal approvals are still pending. Are you sure you want to complete the approval?");
            if (result !== Misc.ModalResult.Yes) {
                return;
            }
        }

        await this.taskRunner.run(async () => {
            if (this.approval) {
                let result = await this.approvalApiClient.complete(this.approval.investorInvestmentApprovalId);
                this.approval.completed = Model.UserEvent.fromJSObject<Model.UserEvent>(result.data);
                this.approval.markOld();
                NotifyUtils.addSuccess("Approval completed successfully.", "", NotificationDuration.Standard);
            }
        });
    }

    public deleteApproval() {
        return this.taskRunner.run(async () => {
            if (this.approval) {
                await this.approvalApiClient.delete(this.approval.investorInvestmentApprovalId);
                this.approval = null;
                NotifyUtils.addSuccess("Approval deleted successfully.", "", NotificationDuration.Standard);
            }
        });
    }

    public mergeTemplateFilter(mergeTemplate: MergeTemplate): boolean {
        return mergeTemplate.isTermsAndConditions 
            && (mergeTemplate.forDwtExempt === null || mergeTemplate.forDwtExempt === this.investorInvestment.lookup.isExempt)
            && (mergeTemplate.forQualifyingPurpose === null || mergeTemplate.forQualifyingPurpose === this.investorInvestment.investorInvestmentDetail.qualifyingPurpose)
            && (mergeTemplate.productTypeId === null || mergeTemplate.productTypeId === this.investorInvestment.investorInvestmentDetail.productTypeId);
    }

    public async beginGenerateTermsAndConditions() {

        const command = new GenerateTermsAndConditionsCommand();
        command.investorInvestmentId = this.investorInvestment.investorInvestmentId;
        command.approvalId = this.approval?.investorInvestmentApprovalId ?? null;

        const mergeTemplates = (await this.dataCache.mergeTemplates.getDataAsync()).filter(this.mergeTemplateFilter);
        if (mergeTemplates.length === 1) {
            command.mergeTemplateId = mergeTemplates[0].mergeTemplateId;
            await this.onMergeTemplateChanged(command, mergeTemplates[0]);
        }
        
        this.termsAndConditionsCommand = command;        
    }

    public async onMergeTemplateChanged(command: GenerateTermsAndConditionsCommand, mergeTemplate?: MergeTemplate) {
        command.signingContacts.set([]);

        if (mergeTemplate) {
            const contactTypeIds = mergeTemplate.signatories.filter(c => c.contactTypeId !== null).map(c => c.contactTypeId!);
            if (contactTypeIds.length > 0) {
                const contacts = await this.contactsApiClient.getFilteredContacts({ contactTypeIds: contactTypeIds });
                this.filteredContacts.set(contacts.data);
            }
            
            for (let signatory of mergeTemplate.signatories) {
                command.addSignatory(signatory.description, signatory.contactTypeId);
            }
        }
    }

    public onContactSelected(signatory: TermsAndConditionsContact, contact?: ContactLookup) {
        signatory.contactTitleId = contact?.titleId ?? null;
        signatory.contactName = contact?.fullName ?? "";
    }

    public async generateTermsAndConditions() {
        if (this.termsAndConditionsCommand) {
            const response = await this.taskRunner.run(() => this.approvalApiClient.generateTermsAndConditions(this.termsAndConditionsCommand!.toJSObject()));

            FileUtils.showSaveFile(response);
            this.termsAndConditionsCommand = null;
        }
    }

    public beginUploadTermsAndConditions() {
        this.termsFileManager.showFileDialog();
    }

    private afterTermsAndConditionsUploaded(response: Components.IFileResponse) {
        if (this.approval) {
            const wasDirty = this.approval.isSelfDirty;
            this.approval.termsAndConditionsFileDescriptorId = response.files[0].fileDescriptorId as number;
            if (!wasDirty) {
                this.approval.isSelfDirty = false;
            }
        }
    }

    public async downloadParticipationAgreementFile() {
        if (this.approval?.participationAgreementFileDescriptorId) {
            var downloadUrl = await this.taskRunner.waitForData(this.documentsApiClient.getFileInlineUrl(this.approval.participationAgreementFileDescriptorId));
            window.open(downloadUrl, "PA", "width=800,height=800");
        }
    }

    public async downloadTermsAndConditionsFile() {
        if (this.approval?.termsAndConditionsFileDescriptorId) {
            var downloadUrl = await this.taskRunner.waitForData(this.documentsApiClient.getInvestorFileUrl(this.approval.termsAndConditionsFileDescriptorId, this.investorInvestment.investorId!));
            window.open(downloadUrl, "TCs", "width=800,height=800");
        }
    }

    public async downloadProofOfPaymentFile() {
        if (this.approval?.proofOfPaymentFileDescriptorId) {
            var downloadUrl = await this.taskRunner.waitForData(this.documentsApiClient.getInvestorFileUrl(this.approval.proofOfPaymentFileDescriptorId, this.investorInvestment.investorId!));
            window.open(downloadUrl, "PoP", "width=800,height=800");
        }
    }
}